import { forwardRef, useState } from 'react'
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'
import { Tooltip } from './Tooltip'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  /** Space Cadet bg, white text — matches prototype btn-primary */
  | 'navy'
  /** Steel Blue bg, white text */
  | 'blue'
  /** Surface-2 bg, text color — matches prototype btn-soft */
  | 'soft'
  /** Space Cadet bg, Pear text — matches prototype btn-ai */
  | 'ai'
  /** White bg, danger fg + tinted border */
  | 'danger-outline'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Node rendered to the left of the label. Replaced by spinner when loading. */
  iconLeft?: ReactNode
  /** Node rendered to the right of the label. */
  iconRight?: ReactNode
  /**
   * Icon-only mode — renders a square button. Caller MUST pass `aria-label`
   * when using this prop; without it the button has no accessible name.
   */
  iconOnly?: boolean
  /** Replaces the leading icon with a spinner and dims the label. Implicitly disables the button. */
  loading?: boolean
  fullWidth?: boolean
  /**
   * When the button is disabled, this message is shown in a tooltip explaining why.
   * Only shown when `disabled` or `loading` is true.
   * e.g. "Complete all required fields before submitting."
   */
  disabledReason?: string
}

// ── size tokens ──────────────────────────────────────────────────
const SIZE_HEIGHT: Record<ButtonSize, number> = { sm: 32, md: 36, lg: 44 }
const SIZE_FONT: Record<ButtonSize, number> = { sm: 12, md: 13, lg: 14 }
const SIZE_PAD_X: Record<ButtonSize, string> = { sm: '8px', md: '12px', lg: '16px' }

// ── variant base styles ───────────────────────────────────────────
function variantBase(variant: ButtonVariant): CSSProperties {
  switch (variant) {
    case 'primary':
      return {
        background: 'var(--steel-blue)',
        color: 'var(--surface)',
        border: 'none',
      }
    case 'secondary':
      return {
        background: 'transparent',
        color: 'var(--text)',
        border: '1px solid var(--border)',
      }
    case 'tertiary':
      return {
        background: 'transparent',
        color: 'var(--text-2)',
        border: 'none',
      }
    case 'destructive':
      return {
        background: 'var(--danger)',
        color: 'var(--surface)',
        border: 'none',
      }
    case 'navy':
      return {
        background: 'var(--space-cadet)',
        color: 'var(--surface)',
        border: 'none',
      }
    case 'blue':
      return {
        background: 'var(--steel-blue)',
        color: 'var(--surface)',
        border: 'none',
      }
    case 'soft':
      return {
        background: 'var(--surface-2)',
        color: 'var(--text)',
        border: 'none',
      }
    case 'ai':
      return {
        background: 'var(--space-cadet)',
        color: 'var(--pear)',
        border: '1px solid var(--space-cadet)',
      }
    case 'danger-outline':
      return {
        background: 'var(--surface)',
        color: 'var(--danger)',
        border: '1px solid rgba(196,68,90,.3)',
      }
  }
}

function variantHover(variant: ButtonVariant): CSSProperties {
  switch (variant) {
    case 'primary':
      return {
        background: 'var(--primary-fg)',
        transform: 'translateY(-1px)',
      }
    case 'secondary':
      return {
        background: 'var(--surface-2)',
        // Use the `border` shorthand (not `borderColor`) to match the base
        // secondary style — mixing the shorthand and longhand for the same box
        // makes React warn when the hover style is toggled on/off.
        border: '1px solid var(--line-strong)',
      }
    case 'tertiary':
      return {
        background: 'var(--surface-2)',
        color: 'var(--text)',
      }
    case 'destructive':
      return {
        filter: 'brightness(0.88)',
      }
    case 'navy':
      return {
        background: 'var(--space-cadet-2)',
      }
    case 'blue':
      return {
        filter: 'brightness(0.92)',
      }
    case 'soft':
      return {
        background: 'var(--french-gray-2)',
      }
    case 'ai':
      return {
        background: 'var(--space-cadet-2)',
      }
    case 'danger-outline':
      return {
        background: 'var(--danger-bg)',
      }
  }
}

// ── spinner ────────────────────────────────────────────────────────
// CSS-only rotating border; prefers-reduced-motion is handled globally
// in tokens.css (sets --motion-duration-fast/base/slow to 0ms and forces
// animation-duration to 1ms), so we don't need a separate JS check here.
const spinnerStyle: CSSProperties = {
  width: 14,
  height: 14,
  borderRadius: '50%',
  border: '2px solid currentColor',
  borderTopColor: 'transparent',
  flexShrink: 0,
  animation: 'lms-btn-spin 600ms linear infinite',
}

/** Build the `transition` shorthand for the given duration. Kept as a single
 *  shorthand (never mixed with a `transitionDuration` longhand) so React
 *  doesn't warn about reconciling shorthand + longhand on the same element. */
function motionTransition(duration: string): string {
  return ['background', 'border-color', 'color', 'transform', 'filter']
    .map((prop) => `${prop} ${duration} var(--motion-ease-out)`)
    .join(', ')
}

/**
 * Brand button primitive. Covers all four semantic variants (primary,
 * secondary, tertiary, destructive) and three sizes (sm, md, lg).
 *
 * When using `iconOnly`, always pass an `aria-label` — the button has no
 * visible text to act as an accessible name without it.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    iconLeft,
    iconRight,
    iconOnly = false,
    loading = false,
    fullWidth = false,
    disabled,
    disabledReason,
    children,
    style,
    ...rest
  },
  ref,
) {
  const [hover, setHover] = useState(false)
  const [pressed, setPressed] = useState(false)

  const isDisabled = disabled || loading
  const height = SIZE_HEIGHT[size]

  const base: CSSProperties = {
    // layout
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    // sizing
    height,
    minHeight: height,
    minWidth: iconOnly ? height : undefined,
    width: fullWidth ? '100%' : undefined,
    padding: iconOnly ? 0 : `0 ${SIZE_PAD_X[size]}`,
    // type
    fontFamily: 'var(--font-sans)',
    fontSize: SIZE_FONT[size],
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    // shape
    borderRadius: 8,
    // interaction
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    // transition
    transition: motionTransition('var(--motion-duration-base)'),
    // disabled
    opacity: isDisabled ? 0.5 : 1,
    // reset browser defaults
    boxSizing: 'border-box',
    textDecoration: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    // touch 44px bump handled in CSS below via data-size attribute
    ...variantBase(variant),
  }

  if (!isDisabled && hover) {
    Object.assign(base, variantHover(variant))
  }

  if (!isDisabled && pressed) {
    base.transform = 'scale(0.98)'
    base.transition = motionTransition('var(--motion-duration-fast)')
  }

  const leadingNode = loading ? (
    <span style={spinnerStyle} aria-hidden />
  ) : (
    iconLeft ?? null
  )

  const btn = (
    <button
      ref={ref}
      type="button"
      data-lms-button
      data-variant={variant}
      data-size={size}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      style={{ ...base, ...style }}
      onMouseEnter={() => !isDisabled && setHover(true)}
      onMouseLeave={() => {
        setHover(false)
        setPressed(false)
      }}
      onMouseDown={() => !isDisabled && setPressed(true)}
      onMouseUp={() => setPressed(false)}
      {...rest}
    >
      {leadingNode}
      {!iconOnly && children != null ? (
        <span style={{ opacity: loading ? 0.7 : 1 }}>{children}</span>
      ) : iconOnly ? (
        children
      ) : null}
      {!loading && iconRight != null ? iconRight : null}
    </button>
  )

  return (
    <>
      {/* Keyframe injected once via a hidden <style> tag scoped to this primitive */}
      <style>{`
        @keyframes lms-btn-spin {
          to { transform: rotate(360deg); }
        }
        /* Touch: bump min-height to 44px regardless of size token */
        @media (hover: none) {
          [data-lms-button] {
            min-height: 44px !important;
          }
        }
      `}</style>
      {isDisabled && disabledReason ? (
        <Tooltip content={disabledReason} placement="top">{btn}</Tooltip>
      ) : (
        btn
      )}
    </>
  )
})
